import { Fragment, useEffect, useState } from 'react';
import { useAtom } from 'ximple';
import { cartAtom } from 'src/state/cart/cartAtom';
import { voucherAtom } from 'src/state/voucher/voucherAtom';
import { Box, capitalize, Grid, Typography } from '@mui/material';
import Payment from 'src/components/domain/payment/Payment';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { useLocale } from '@repo/i18n';
import { GiftcardPaymentInfo, OctoOrder, PaymentInfo } from '@repo/types';
import Receipt from '../../receipt/Receipt';
import TypographyStyleUtils from 'src/components/utils/jss/TypographyStyleUtils';
import { useCartCheckoutContext } from '../CartCheckoutContext';
import CheckoutSummary, { useSummaryFeatures } from '../../CheckoutSummary';
import useCartItemsForSummary from 'src/hooks/domain/useCartItemsForSummary';
import { useConfirmedOctoOrder } from '@repo/widget-utils/services/hooks/octo/index';

type PaymentViewProps = {
    onPaymentCancelled: () => void;
    onPaymentCompleted: (
        paymentInfo: PaymentInfo | GiftcardPaymentInfo,
        paymentGatewayResponse: any,
    ) => void;
};

export function PaymentView({ onPaymentCancelled, onPaymentCompleted }: PaymentViewProps) {
    const { t } = useLocale();
    const [cartItems] = useAtom(cartAtom);
    const [{ appliedPromoCode }] = useAtom(voucherAtom);
    const { activeTab, paymentInfo, largeScreen, appliedGiftcards, completedPaymentInfo } =
        useCartCheckoutContext();

    const cartItemsForSummary = useCartItemsForSummary();

    const showShare = false;
    const enableCartClearing = false;
    const enableItemEditing = false;

    const summaryFeatures = useSummaryFeatures(
        showShare,
        enableCartClearing,
        enableItemEditing,
        activeTab,
        cartItemsForSummary.isWaitingListMode,
    );

    const initialConfirmedOctoOrder =
        completedPaymentInfo?.octoOrder && completedPaymentInfo.octoOrder.confirmedAt
            ? completedPaymentInfo.octoOrder
            : undefined;

    const [confirmedOctoOrder, setConfirmedOctoOrder] = useState<OctoOrder | undefined>(
        initialConfirmedOctoOrder,
    );

    useWidgetEventEffect(
        () => ({
            eventType: 'checkoutStep',
            checkoutStep: 'Payment',
            cartItems: Object.values(cartItems),
            giftCard: appliedGiftcards
                ? appliedGiftcards.map((appliedGiftcard) => ({
                      giftcardReference: appliedGiftcard.giftcardStatus.id,
                      priceReduction: appliedGiftcard.priceReduction,
                  }))
                : null,
            promoCode: appliedPromoCode,
        }),
        () => !!completedPaymentInfo,
    );

    const octoOrderToConfirm =
        !confirmedOctoOrder &&
        completedPaymentInfo?.octoOrder &&
        !completedPaymentInfo.octoOrder.confirmedAt
            ? completedPaymentInfo.octoOrder
            : undefined;

    const { data: octoOrderConfirmedNow } = useConfirmedOctoOrder(octoOrderToConfirm);

    useEffect(() => {
        if (octoOrderConfirmedNow) {
            setConfirmedOctoOrder(octoOrderConfirmedNow);
        }
    }, [octoOrderConfirmedNow]);

    return (
        <Fragment>
            {paymentInfo && (
                <Fragment>
                    <Grid
                        container
                        justifyContent={largeScreen ? 'space-between' : 'center'}
                        alignItems={largeScreen ? 'flex-start' : 'center'}
                        direction={largeScreen ? 'row' : 'column-reverse'}
                    >
                        <Box sx={{ width: largeScreen ? '50%' : '100%' }}>
                            <Payment
                                onPaymentCancelled={onPaymentCancelled}
                                onPaymentCompleted={onPaymentCompleted}
                                paymentInfo={paymentInfo}
                            />
                        </Box>
                        <CheckoutSummary
                            cartItems={cartItemsForSummary.itemsToShow}
                            hideGoPay={true}
                            summaryFeatures={summaryFeatures}
                            completedOctoOrder={octoOrderConfirmedNow}
                        />
                    </Grid>
                </Fragment>
            )}
            {completedPaymentInfo && (
                <Fragment>
                    <Grid container justifyContent={largeScreen ? 'flex-start' : 'center'}>
                        <Typography variant="h3" sx={TypographyStyleUtils.secondaryTextColorSx}>
                            {capitalize(t.thank_you_for_booking_with_us)}
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        justifyContent={largeScreen ? 'space-between' : 'center'}
                        alignItems={largeScreen ? 'flex-start' : 'center'}
                        direction={largeScreen ? 'row' : 'column-reverse'}
                    >
                        <Receipt bookingRef={completedPaymentInfo.referenceId} />

                        {completedPaymentInfo.purchasedCartItems && (
                            <CheckoutSummary
                                showPrintButton={true}
                                cartItems={completedPaymentInfo.purchasedCartItems}
                                hideGoPay={true}
                                summaryFeatures={summaryFeatures}
                                completedOctoOrder={completedPaymentInfo.octoOrder}
                                bookingRef={completedPaymentInfo.referenceId}
                            />
                        )}
                    </Grid>
                </Fragment>
            )}
        </Fragment>
    );
}
